/**
 * /wireline-slickline/tickets/:id — one Wireline & Slickline field ticket (fieldiq-api#1345,
 * WL-4). Well Servicing's ticket screen, carried over: this vertical's API
 * mirrors WS-4. The ticket names its unit (a per-day ticket is per unit per
 * day) and lists the day sheets it covers, each a link to the sheet. The
 * money is the API's subtotal; tax is the invoice's (WL-5). A customer-approved
 * ticket says which live invoice holds it, or links to New invoice.
 *
 * WHAT MAY BE EDITED (wlTicketEditMode, mirroring the API):
 *   draft              lines and header edit freely.
 *   office_approved,   an edit returns the ticket to draft. The screen does
 *   customer_rejected  not offer the edit until the office says "unlock" to a
 *                      sentence saying so; when a write then comes back
 *                      `reverted_to_draft: true` the screen says that too.
 *   customer_approved  frozen. Only the internal notes change.
 *   void               frozen; only the internal notes change. It says who voided it
 *                      and why. Void is offered from every other status, with
 *                      a required reason, and is confirmed inline.
 *
 * Refresh from the day sheets redraws the 'auto' lines and says FIRST that an
 * office price on one of them is lost (office lines are kept). The PDF is
 * offered once the office has signed (a draft or void ticket is 409). No
 * modals: every confirm is inline and every refusal is a sentence in the
 * refusal style.
 *
 * Write controls (refresh, lines, details, approve, send, record, void,
 * delete) are drawn for WL_TICKET_WRITE_ROLES only — the API refuses every
 * other role; a supervisor or dispatcher reads the ticket.
 */
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Ban, FileText, Lock, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, getBlobApiErrorMessage } from '../../../api/client';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { useWlMeta } from '../../../hooks/useWirelineSlickline';
import { useDeleteWlTicket, useWlRates, useWlTicket, useRefreshWlTicket, useUpdateWlTicket, useVoidWlTicket, type WlTicketEditResult } from '../../../hooks/useWlTickets';
import { claimBlobTab, POPUP_BLOCKED_MESSAGE, showBlobInTab } from '../../../lib/blob-tab';
import { formatInstant } from '../../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import {
  canWriteWlTickets,
  wlScopeText,
  wlTicketCoverText,
  wlTicketDeletable,
  wlTicketEditMode,
  wlTicketHasPdf,
  wlTicketInvoicedFrom,
  wlTicketRefusal,
  wlTicketStatusSkin,
  wlTicketWellText,
  WL_REFRESH_WARNING,
  WL_VOID_WARNING,
} from '../../../lib/wireline-slickline-tickets';
import { wlBlankToNull } from '../../../lib/wireline-slickline';
import { wlDay, wlSheetStatusSkin } from '../../../lib/wireline-slickline-jobs';
import type { WlTicket, WlTicketCatalog, WlTicketHeaderWrite } from '../../../types/wireline-slickline-tickets';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useBackToList } from '../../downhole/recordPage';
import { isNotFound, WL_EYEBROW, WL_INPUT, WL_PRIMARY, WL_SECONDARY, WlBadge, WlEmpty, WlField, WlFormError, WlLoading, WlPanel, WlSpec } from '../WlBits';
import { PfInlineConfirm as WlInlineConfirm } from '../../pipeline-facility/digs/DigBits';
import { WlRevertedNotice } from './TicketBits';
import { WlTicketLines } from './TicketLines';
import { WlTicketSignOff } from './TicketSignOff';
import { WlTicketInvoiceLink } from '../invoices/InvoiceBits';
import { canUseWlAi, wlTicketPreflightable } from '../../../lib/wl-ai';
import { WlAiLauncher } from '../ai/WlAiBits';

const HEADER_FIELDS: { key: keyof WlTicketHeaderWrite; read: keyof WlTicket; label: string; max: number }[] = [
  { key: 'afe_number', read: 'afeNumber', label: 'AFE number', max: 100 },
  { key: 'po_number', read: 'poNumber', label: 'PO number', max: 100 },
  { key: 'cost_code', read: 'costCode', label: 'Cost code', max: 100 },
];

function HeaderForm({ ticket, pending, error, onCancel, onSubmit }: {
  ticket: WlTicket;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (body: WlTicketHeaderWrite) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const v: Record<string, string> = { customer_notes: ticket.customerNotes ?? '' };
    HEADER_FIELDS.forEach((f) => { v[f.key] = (ticket[f.read] as string | null) ?? ''; });
    return v;
  });
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setValues((v) => ({ ...v, [k]: e.target.value }));
  return (
    <div data-testid="wl-ticket-header-form" className="space-y-3 border-t border-fieldiq-border px-4 py-4">
      <WlFormError message={error} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {HEADER_FIELDS.map((f) => (
          <WlField key={f.key} label={f.label}>
            <input className={WL_INPUT} maxLength={f.max} value={values[f.key]} onChange={set(f.key)} />
          </WlField>
        ))}
      </div>
      <WlField label="Notes for the customer" hint="Printed on the ticket.">
        <textarea className={`${WL_INPUT} h-24 py-2`} maxLength={5000} value={values.customer_notes} onChange={set('customer_notes')} />
      </WlField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WL_SECONDARY} onClick={onCancel} disabled={pending}>Cancel</button>
        <button
          type="button"
          className={WL_PRIMARY}
          disabled={pending}
          onClick={() => {
            const body: WlTicketHeaderWrite = {};
            const cur: Record<string, string | null> = { customer_notes: ticket.customerNotes };
            HEADER_FIELDS.forEach((f) => { cur[f.key] = ticket[f.read] as string | null; });
            Object.keys(values).forEach((k) => {
              const next = wlBlankToNull(values[k]);
              if (next !== (cur[k] ?? null)) (body as Record<string, string | null>)[k] = next;
            });
            onSubmit(body);
          }}
        >
          {pending ? 'Saving…' : 'Save details'}
        </button>
      </div>
    </div>
  );
}

/**
 * Void: a required reason, said before the office presses it — the button
 * refuses an empty reason itself, and a 400 `reason_required` still reads as a sentence.
 * A ticket on a live invoice is refused (409 `ticket_invoiced`, WL-5): the sentence names the invoice and
 * the page links to it, since the invoice is what has to be voided first.
 */
function VoidTicket({ ticket, onCancel, onDone }: { ticket: WlTicket; onCancel: () => void; onDone: () => void }) {
  const voidTicket = useVoidWlTicket();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [onInvoice, setOnInvoice] = useState<{ id: string; number: string } | null>(null);
  return (
    <div data-testid="wl-ticket-void" role="group" aria-label="Void this ticket" className="space-y-3 rounded-fi-control border border-fi-red/50 bg-fi-signal-red/10 px-4 py-4">
      <p className="text-sm text-fieldiq-text">{WL_VOID_WARNING}</p>
      <WlFormError message={error} />
      {onInvoice && (
        <p className="text-sm text-fieldiq-text" data-testid="wl-ticket-void-invoice">
          Open{' '}
          <Link className="font-mono font-bold underline" to={`/wireline-slickline/invoices/${onInvoice.id}`}>
            {onInvoice.number}
          </Link>{' '}
          to void it.
        </p>
      )}
      <WlField label="Why is it being voided? *">
        <textarea className={`${WL_INPUT} h-20 py-2`} maxLength={2000} aria-label="Reason for voiding" value={reason} onChange={(e) => setReason(e.target.value)} />
      </WlField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WL_SECONDARY} onClick={onCancel} disabled={voidTicket.isPending}>Keep it</button>
        <button
          type="button"
          className={WL_PRIMARY}
          disabled={voidTicket.isPending}
          onClick={() => {
            setError(null);
            setOnInvoice(null);
            if (!reason.trim()) return setError('Say why the ticket is being voided.');
            voidTicket.mutate(
              { id: ticket.id, reason: reason.trim() },
              {
                onSuccess: () => { toast.success(`${ticket.ticketNumber} voided`); onDone(); },
                onError: (e) => {
                  setError(wlTicketRefusal(e, 'The ticket could not be voided.'));
                  setOnInvoice(wlTicketInvoicedFrom(e));
                },
              },
            );
          }}
        >
          {voidTicket.isPending ? 'Voiding…' : 'Void ticket'}
        </button>
      </div>
    </div>
  );
}

function InternalNotes({ ticket, canWrite }: { ticket: WlTicket; canWrite: boolean }) {
  const update = useUpdateWlTicket();
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(ticket.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  return (
    <WlPanel
      eyebrow="Internal"
      title="Office notes"
      testId="wl-ticket-notes"
      actions={canWrite && !editing ? <button type="button" className={WL_SECONDARY} onClick={() => { setNotes(ticket.notes ?? ''); setError(null); setEditing(true); }}>Edit notes</button> : undefined}
    >
      {editing ? (
        <div className="space-y-3 px-4 py-4">
          <WlFormError message={error} />
          <WlField label="Notes" hint="Never printed on the ticket, so they can change at any status, void included.">
            <textarea className={`${WL_INPUT} h-24 py-2`} aria-label="Notes" maxLength={5000} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </WlField>
          <div className="flex justify-end gap-2">
            <button type="button" className={WL_SECONDARY} onClick={() => setEditing(false)} disabled={update.isPending}>Cancel</button>
            <button
              type="button"
              className={WL_PRIMARY}
              disabled={update.isPending}
              onClick={() =>
                update.mutate(
                  { id: ticket.id, body: { notes: wlBlankToNull(notes) } },
                  { onSuccess: () => { toast.success('Notes saved'); setEditing(false); }, onError: (e) => setError(wlTicketRefusal(e, 'The notes could not be saved.')) },
                )
              }
            >
              {update.isPending ? 'Saving…' : 'Save notes'}
            </button>
          </div>
        </div>
      ) : (
        <p className="whitespace-pre-wrap px-4 py-4 text-sm text-fieldiq-text">{ticket.notes || '—'}</p>
      )}
    </WlPanel>
  );
}

/** The day sheets this ticket covers (still linked), oldest first — the API's `sheets`. */
const liveSheets = (t: WlTicket) => (t.sheets ?? []).filter((x) => !x.releasedAt);

/** `12 Oct 2026` or `12 Oct 2026 – 15 Oct 2026` from the linked sheets; `—` when it covers none. */
export function sheetDays(t: WlTicket): string {
  if (t.scope === 'day' && t.workDate) return wlDay(t.workDate);
  const days = liveSheets(t).map((x) => x.workDate).filter((d): d is string => !!d).sort();
  if (!days.length) return '—';
  return days[0] === days[days.length - 1] ? wlDay(days[0]) : `${wlDay(days[0])} – ${wlDay(days[days.length - 1])}`;
}

function TicketSheets({ ticket }: { ticket: WlTicket }) {
  const sheets = liveSheets(ticket);
  return (
    <div data-testid="wl-ticket-sheets" className="border-t border-fieldiq-border px-4 py-3">
      <p className="mb-2 text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Day sheets on this ticket</p>
      {!sheets.length ? (
        <p className="text-sm text-fieldiq-muted">—</p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {sheets.map((x) => (
            <li key={x.id}>
              <Link className="inline-flex items-center gap-2 rounded-fi-control border border-fieldiq-border px-2 py-1 text-sm text-fieldiq-text underline" to={`/wireline-slickline/sheets/${x.id}`}>
                {wlDay(x.workDate)}
                {x.unitNumber ? <span className="font-mono">{x.unitNumber}</span> : null}
                <WlBadge skin={wlSheetStatusSkin(x.status)} value={x.status ?? ''} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function WlTicketDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/wireline-slickline/tickets');
  const { user } = useAuth();
  const canWrite = canWriteWlTickets(user?.role);
  const query = useWlTicket(id);
  const catalog = (useWlMeta().data?.data ?? null) as WlTicketCatalog | null;
  const ratesQuery = useWlRates(undefined, canWrite);
  const update = useUpdateWlTicket();
  const refresh = useRefreshWlTicket();
  const del = useDeleteWlTicket();
  const [reverted, setReverted] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerError, setHeaderError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [unpricedIds, setUnpricedIds] = useState<string[]>([]);
  const [voiding, setVoiding] = useState(false);
  const t = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const tz = meta?.timeZone ?? null;
  const failure = queryLoadFailure(query);
  const mode = wlTicketEditMode(t?.status);
  const editable = canWrite && (mode === 'free' || (mode === 'reverts' && unlocked));

  const onEdited = (r: WlTicketEditResult) => {
    if (r.reverted) {
      setReverted(true);
      setUnlocked(false);
    }
  };

  const openPdf = async () => {
    if (!t) return;
    setPdfError(null);
    const win = claimBlobTab();
    if (!win) return setPdfError(POPUP_BLOCKED_MESSAGE);
    setPdfBusy(true);
    try {
      const res = await api.wirelineSlickline.tickets.pdf(t.id);
      showBlobInTab(win, res.data);
    } catch (e) {
      win.close();
      setPdfError(await getBlobApiErrorMessage(e, 'The PDF could not be made.'));
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div data-testid="wl-ticket-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WL_EYEBROW}
        title={t?.ticketNumber || (query.isLoading ? 'Field ticket' : '—')}
        description={t ? `${t.customerName ?? '—'} · ${wlTicketWellText(t)} · ${t.jobNumber ?? '—'} · ${wlTicketCoverText(t)}` : undefined}
        onBack={back}
        backLabel="Back"
        actions={
          t ? (
            <div className="flex flex-wrap gap-2">
              {wlTicketHasPdf(t.status) && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={openPdf} disabled={pdfBusy}>
                  <FileText className="size-4" aria-hidden="true" />
                  {pdfBusy ? 'Opening…' : 'PDF'}
                </button>
              )}
              {canWrite && t.status !== 'void' && (
                <button type="button" className={DH_BAND_SECONDARY} data-testid="wl-ticket-void-open" onClick={() => setVoiding(true)} disabled={voiding}>
                  <Ban className="size-4" aria-hidden="true" />
                  Void
                </button>
              )}
              {canWrite && wlTicketDeletable(t.status) && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WlLoading label="Loading ticket" />
          ) : isNotFound(queryFailureError(query)) || (!failure && !t) ? (
            <WlPanel eyebrow="Field ticket" title="Field ticket">
              <WlEmpty>This ticket was not found. It may have been deleted.</WlEmpty>
            </WlPanel>
          ) : failure || !t ? (
            <WlPanel eyebrow="Field ticket" title="Field ticket">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this ticket" />
            </WlPanel>
          ) : (
            <>
              <WlFormError message={pdfError} />
              {voiding && canWrite && t.status !== 'void' && <VoidTicket ticket={t} onCancel={() => setVoiding(false)} onDone={() => setVoiding(false)} />}
              {reverted && <WlRevertedNotice onDismiss={() => setReverted(false)} />}
              {deleting && (
                <WlInlineConfirm
                  testId="wl-ticket-delete-confirm"
                  message={`Delete ${t.ticketNumber}? Its lines go with it. The job's day sheets are not touched, so it can be drafted again.`}
                  confirmLabel="Delete ticket"
                  cancelLabel="Keep it"
                  pending={del.isPending}
                  error={deleteError}
                  onCancel={() => setDeleting(false)}
                  onConfirm={() =>
                    del.mutate(t.id, {
                      onSuccess: () => {
                        toast.success(`${t.ticketNumber} deleted`);
                        navigate('/wireline-slickline/tickets', { replace: true });
                      },
                      onError: (e) => setDeleteError(wlTicketRefusal(e, 'The ticket could not be deleted.')),
                    })
                  }
                />
              )}
              {canWrite && mode === 'reverts' && !unlocked && (
                <div className="flex flex-wrap items-center gap-3">
                  {unlocking ? (
                    <WlInlineConfirm
                      testId="wl-ticket-unlock-confirm"
                      message="Editing this ticket returns it to draft: the office approval is withdrawn, and any review link sent to the customer stops working. It has to be approved and sent again."
                      confirmLabel="Unlock to edit"
                      cancelLabel="Leave it signed"
                      onCancel={() => setUnlocking(false)}
                      onConfirm={() => { setUnlocked(true); setUnlocking(false); }}
                    />
                  ) : (
                    <button type="button" className={WL_SECONDARY} onClick={() => setUnlocking(true)} data-testid="wl-ticket-unlock">
                      <Lock className="size-4" aria-hidden="true" />
                      Edit this ticket
                    </button>
                  )}
                </div>
              )}
              {mode === 'frozen' && (
                <p data-testid="wl-ticket-frozen" className="text-sm text-fieldiq-muted">
                  The customer has approved this ticket, so what it bills cannot change. Office notes can still be edited.
                </p>
              )}
              {mode === 'void' && (
                <p data-testid="wl-ticket-voided" role="status" className="rounded-fi-control border border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                  Void{t.voidedByName ? ` — voided by ${t.voidedByName}` : ''}
                  {t.voidedAt ? ` on ${formatInstant(t.voidedAt, tz) ?? '—'}` : ''}. Reason: {t.voidReason || '—'}. A void ticket bills nothing and
                  cannot be changed.
                </p>
              )}
              <WlPanel
                eyebrow="Field ticket"
                title="Details"
                testId="wl-ticket-details"
                actions={editable && !editingHeader ? <button type="button" className={WL_SECONDARY} onClick={() => { setHeaderError(null); setEditingHeader(true); }}>Edit details</button> : undefined}
              >
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <WlSpec label="Status" value={<WlBadge skin={wlTicketStatusSkin(t.status)} value={t.status} />} />
                  <WlSpec label="Job" value={t.jobNumber ? <Link className="underline" to={`/wireline-slickline/jobs/${t.jobId}`}>{t.jobNumber}</Link> : '—'} mono />
                  <WlSpec label="Customer" value={t.customerName || '—'} />
                  <WlSpec label="Covers" value={wlScopeText(t)} />
                  <WlSpec label="Well" value={wlTicketWellText(t)} />
                  <WlSpec label="Unit" value={t.unitNumber || '—'} mono />
                  <WlSpec label="Days" value={sheetDays(t)} />
                  <WlSpec label="Province / state" value={t.provinceStateName || t.provinceState || '—'} />
                  <WlSpec label="Currency" value={t.currency || '—'} />
                  <WlSpec label="Created" value={t.createdAt ? formatInstant(t.createdAt, tz) ?? '—' : '—'} />
                  {HEADER_FIELDS.map((f) => (
                    <WlSpec key={f.key} label={f.label} value={(t[f.read] as string | null) || '—'} />
                  ))}
                </dl>
                {t.customerNotes && <p className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{t.customerNotes}</p>}
                <TicketSheets ticket={t} />
                {editable && editingHeader && (
                  <HeaderForm
                    ticket={t}
                    pending={update.isPending}
                    error={headerError}
                    onCancel={() => setEditingHeader(false)}
                    onSubmit={(body) => {
                      if (!Object.keys(body).length) return setHeaderError('Nothing has changed.');
                      setHeaderError(null);
                      update.mutate({ id: t.id, body }, {
                        onSuccess: (r) => { toast.success('Details saved'); setEditingHeader(false); onEdited(r); },
                        onError: (e) => setHeaderError(wlTicketRefusal(e, 'The details could not be saved.')),
                      });
                    }}
                  />
                )}
              </WlPanel>
              {editable && (
                <div className="space-y-2">
                  {refreshing ? (
                    <WlInlineConfirm
                      testId="wl-ticket-refresh-confirm"
                      message={WL_REFRESH_WARNING}
                      confirmLabel="Refresh from the day sheets"
                      cancelLabel="Not now"
                      pending={refresh.isPending}
                      error={refreshError}
                      onCancel={() => setRefreshing(false)}
                      onConfirm={() =>
                        refresh.mutate(t.id, {
                          onSuccess: (r) => { toast.success('Lines redrawn from the day sheets'); setRefreshing(false); onEdited(r); },
                          onError: (e) => setRefreshError(wlTicketRefusal(e, 'The ticket could not be refreshed.')),
                        })
                      }
                    />
                  ) : (
                    <button type="button" className={WL_SECONDARY} data-testid="wl-ticket-refresh" onClick={() => { setRefreshError(null); setRefreshing(true); }}>
                      <RefreshCw className="size-4" aria-hidden="true" />
                      Refresh from the day sheets
                    </button>
                  )}
                </div>
              )}
              <WlTicketLines
                ticket={t}
                editable={editable}
                canWrite={canWrite}
                rates={ratesQuery.data?.data ?? []}
                catalog={catalog}
                highlight={unpricedIds}
                onEdited={onEdited}
              />
              {t.status === 'customer_approved' && <WlTicketInvoiceLink ticket={t} />}
              <WlTicketSignOff ticket={t} canWrite={canWrite} tz={tz} today={meta?.today ?? null} onUnpriced={setUnpricedIds} />
              <InternalNotes ticket={t} canWrite={canWrite} />
              {canUseWlAi(user?.role) && wlTicketPreflightable(t.status) && (
                <WlAiLauncher
                  key={t.id}
                  request={{ agent: 'wl_ticket_preflight', ticketId: t.id }}
                  eyebrow="Before sending"
                  title="Ticket preflight"
                  blurb="Checks this ticket for unpriced lines and the job's approved day sheets for run kinds with no rate. The AI's text explains the flags; it changes nothing."
                  testId="wl-ai-ticket-preflight"
                />
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
